import React, { createContext, useContext, useState, useEffect } from "react";
import authService from "../services/auth.service";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function signup(email, password, fullName = "") {
    try {
      const user = await authService.registerWithEmailPassword(
        email,
        password,
        fullName
      );
      return user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function login(email, password) {
    try {
      const user = await authService.signInWithEmailPassword(email, password);
      return user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function loginWithGoogle() {
    try {
      return await authService.signInWithGoogle();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function logout() {
    try {
      await authService.signOut();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function resetPassword(email) {
    try {
      return await authService.resetPassword(email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function updatePassword(newPassword) {
    try {
      return await authService.updatePassword(newPassword);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  useEffect(() => {
    const unsubscribe = authService.initAuthListener();

    // Add observer to update our React state
    authService.addAuthStateObserver((user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return () => {
      // Clean up the observer when the component unmounts
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, []);

  const value = {
    currentUser,
    error,
    setError,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    updatePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
